import { useState, type ReactElement } from "react";
import { ChevronLeft } from "lucide-react";

import {
  applyLayoutMovesToTemplateSource,
  applyZplLayoutPatches,
  listZplLayoutItems,
  type ZplLayoutItem,
} from "@integrator-src/api/zpl-layout-editor";
import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import {
  LabelPreviewRotationSelect,
  LabelPreviewWithRulers,
  type LabelPreviewRotationDeg,
} from "@/pages/labelTemplates/LabelPreviewWithRulers";

export function LabelTemplateLayoutEditor(props: {
  sourceZpl: string;
  sourceTemplate: string;
  previewImageSrc: string | null;
  previewRulerSize?: { widthInches: number; heightInches: number } | null;
  viewRotationDeg?: LabelPreviewRotationDeg;
  onViewRotationDegChange?: (value: LabelPreviewRotationDeg) => void;
  previewBusy: boolean;
  onPreviewPatchedZpl: (zpl: string) => void;
  onClose: (result: { zpl: string; template: string } | null) => void;
  saveGitPending?: boolean;
  copyPending?: boolean;
  onSaveGit?: (result: { zpl: string; template: string }) => void;
  onCopySupabase?: (result: { zpl: string; template: string }) => void;
}): ReactElement {
  const baselineZpl = props.sourceZpl;
  const [items, setItems] = useState<ZplLayoutItem[]>(() => listZplLayoutItems(props.sourceZpl));
  const originalItems = useState(() => listZplLayoutItems(props.sourceZpl))[0];

  function patchedZplFrom(next: ZplLayoutItem[] = items): string {
    return applyZplLayoutPatches(baselineZpl, next);
  }

  function patchedTemplateFrom(next: ZplLayoutItem[] = items): string {
    if (props.sourceTemplate.trim() === "") return props.sourceTemplate;
    return applyLayoutMovesToTemplateSource(props.sourceTemplate, originalItems, next);
  }

  function updateItem(id: string, patch: Partial<Pick<ZplLayoutItem, "x" | "y" | "size" | "name">>): void {
    setItems((current) => current.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="-ml-2"
              onClick={() =>
                props.onClose({ zpl: patchedZplFrom(), template: patchedTemplateFrom() })
              }
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
              Back
            </Button>
            <h3 className="text-sm font-semibold text-slate-900">Layout editor</h3>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Change size, X, and Y. Refresh preview checks the picture. Save Git template writes the
            moved fields to the shared .hbs.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {props.onViewRotationDegChange != null ? (
            <LabelPreviewRotationSelect
              id="layout-preview-view-rotation"
              value={props.viewRotationDeg ?? 0}
              onChange={props.onViewRotationDegChange}
            />
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="secondary"
            isLoading={props.previewBusy}
            onClick={() => props.onPreviewPatchedZpl(patchedZplFrom())}
          >
            Refresh preview
          </Button>
          <Button type="button" size="sm" variant="secondary" onClick={() => props.onClose(null)}>
            Discard layout edits
          </Button>
          {props.onSaveGit != null ? (
            <Button
              type="button"
              size="sm"
              isLoading={props.saveGitPending}
              title="Writes these coordinates into the Git .hbs for this family/version."
              onClick={() => {
                const result = { zpl: patchedZplFrom(), template: patchedTemplateFrom() };
                props.onSaveGit?.(result);
                props.onClose(result);
              }}
            >
              Save Git template
            </Button>
          ) : null}
          {props.onCopySupabase != null ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              isLoading={props.copyPending}
              title="Optional database snapshot. Floor print still reads Git."
              onClick={() =>
                props.onCopySupabase?.({ zpl: patchedZplFrom(), template: patchedTemplateFrom() })
              }
            >
              Copy to Supabase
            </Button>
          ) : null}
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(280px,22rem)]">
        <div className="relative flex min-h-[24rem] items-center justify-center overflow-auto rounded-lg border border-slate-200 bg-white p-3">
          {props.previewImageSrc != null ? (
            props.previewRulerSize != null ? (
              <LabelPreviewWithRulers
                src={props.previewImageSrc}
                alt="Label layout preview"
                widthInches={props.previewRulerSize.widthInches}
                heightInches={props.previewRulerSize.heightInches}
                rotationDeg={props.viewRotationDeg ?? 0}
                maxHeightClassName="max-h-[36rem]"
              />
            ) : (
              <img
                src={props.previewImageSrc}
                alt="Label layout preview"
                className="max-h-[36rem] max-w-full border border-dashed border-slate-400 object-contain"
                style={{ transform: `rotate(${String(props.viewRotationDeg ?? 0)}deg)` }}
              />
            )
          ) : (
            <p className="text-sm text-slate-500">Render a preview first, then edit layout.</p>
          )}
          {props.previewBusy ? (
            <p className="absolute bottom-3 right-3 rounded bg-white/90 px-2 py-1 text-xs text-slate-600">
              Updating preview…
            </p>
          ) : null}
        </div>

        <div className="max-h-[36rem] space-y-3 overflow-auto">
          {items.length === 0 ? (
            <p className="text-sm text-slate-500">No movable ^FO fields in this ZPL yet.</p>
          ) : (
            items.map((item) => (
              <article key={item.id} className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-xs font-medium text-slate-800">{item.name || item.kind}</p>
                {item.font ? <p className="mt-0.5 text-[11px] text-slate-500">Font: {item.font}</p> : null}
                <div className="mt-2 grid grid-cols-3 gap-2">
                  <div>
                    <Label htmlFor={`${item.id}-size`} className="text-[11px]">
                      Size
                    </Label>
                    <Input
                      id={`${item.id}-size`}
                      type="number"
                      min={1}
                      step={1}
                      className="mt-1 h-8"
                      value={item.size}
                      onChange={(event) => updateItem(item.id, { size: Number(event.target.value) })}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`${item.id}-x`} className="text-[11px]">
                      X
                    </Label>
                    <Input
                      id={`${item.id}-x`}
                      type="number"
                      step={1}
                      className="mt-1 h-8"
                      value={item.x}
                      onChange={(event) => updateItem(item.id, { x: Number(event.target.value) })}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`${item.id}-y`} className="text-[11px]">
                      Y
                    </Label>
                    <Input
                      id={`${item.id}-y`}
                      type="number"
                      step={1}
                      className="mt-1 h-8"
                      value={item.y}
                      onChange={(event) => updateItem(item.id, { y: Number(event.target.value) })}
                    />
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
